import { resolveSchema, resolveDomainEvent } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import {
  buildSchemaField,
  setTag,
  resolveFieldRefs,
  resolveFieldUpdateRefs,
  buildEntityRef,
  hasIdField,
  normalizeDescription
} from './schemaFields.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType, MAX_DESCRIPTION_LENGTH } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

export const buildCreateEntitiesMutation = ({ workflow, workflowId, entities, cardTypes }) => {
  if (!Array.isArray(entities) || entities.length === 0) {
    return { error: 'entities is required and must be a non-empty array' }
  }

  const existingEntityNames = new Set(
    (workflow.schemas || [])
      .filter((schema) => schema.type === SchemaType.ENTITY)
      .map((schema) => schema.name)
  )
  const batchNames = new Set()
  const newSchemas = []
  const aggregateRootAssignments = []
  const claimedEventIds = new Set()
  const skippedNames = []

  for (const entity of entities) {
    const { name, description, boundedContext, fields, aggregateRootFor } = entity || {}

    if (!name || typeof name !== 'string' || name.trim() === '') {
      return { error: 'Each entity must have a non-empty name' }
    }

    if (batchNames.has(name)) {
      return { error: `Duplicate entity name "${name}" in the batch` }
    }
    batchNames.add(name)

    // Already existing is not a failure — the entity is there, which is what was wanted. Skip
    // it and keep the rest of the batch, but say so: its fields were left untouched.
    if (existingEntityNames.has(name)) {
      skippedNames.push(name)
      continue
    }

    const normalizedDescription = normalizeDescription(description, MAX_DESCRIPTION_LENGTH)
    if (normalizedDescription.error) {
      return { error: `Entity "${name}": ${normalizedDescription.error}` }
    }

    let resolvedBoundedContextId
    if (boundedContext) {
      const matchedBoundedContext = (workflow.boundedContexts || []).find((context) => context.name === boundedContext)
      if (!matchedBoundedContext) {
        return { error: `Bounded context "${boundedContext}" not found for entity "${name}"` }
      }
      resolvedBoundedContextId = matchedBoundedContext.id
    }

    const resolvedFields = resolveFieldRefs(fields, workflow)

    if (Array.isArray(aggregateRootFor) && aggregateRootFor.length > 0) {
      if (!hasIdField(fields)) {
        return { error: `Entity "${name}" cannot be an aggregate root because it has no "id" field (value objects cannot be aggregate roots)` }
      }

      for (const eventRef of aggregateRootFor) {
        const event = resolveDomainEvent(eventRef, workflow)
        if (!event) {
          return { error: `Domain event not found for ref: ${eventRef} (referenced by entity "${name}")` }
        }
        if (claimedEventIds.has(event.id)) {
          return { error: `Domain event "${event.description}" is claimed as aggregate root by more than one entity in the batch` }
        }
        claimedEventIds.add(event.id)

        aggregateRootAssignments.push({ entityName: name, event })
      }
    }

    newSchemas.push({
      id: generateId(),
      name,
      description: normalizedDescription.value,
      type: SchemaType.ENTITY,
      boundedContext: resolvedBoundedContextId || undefined,
      fields: (resolvedFields || []).map((field) => buildSchemaField(field, SchemaType.ENTITY)),
      index: -1
    })
  }

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  let cardInput = null

  if (aggregateRootAssignments.length > 0) {
    const cardTypesList = cardTypes || []
    const cardTypesMap = Object.fromEntries(cardTypesList.map((cardType) => [cardType.id, cardType]))
    const aggregateRootCardType = cardTypesList.find((cardType) => cardType.domainModelRole === DomainModelRole.AGGREGATE_ROOT)

    if (!aggregateRootCardType) {
      return { error: 'Aggregate root card type not found in this workflow' }
    }

    for (const assignment of aggregateRootAssignments) {
      const existingAggregateRoot = (assignment.event.cards || []).find((card) => {
        const cardType = cardTypesMap[card.cardTypeId]

        return cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
      })
      if (existingAggregateRoot) {
        return { error: `Domain event "${assignment.event.description}" already has an aggregate root card` }
      }
    }

    const schemaByName = new Map(newSchemas.map((schema) => [schema.name, schema]))
    const domainEvents = workflow.domainEvents || []
    let lastSortkey = getLastCardSortkey(domainEvents)
    const newCards = []

    for (const { entityName, event } of aggregateRootAssignments) {
      const schema = schemaByName.get(entityName)
      const eventIndex = domainEvents.findIndex((domainEvent) => domainEvent.id === event.id)
      const sortkey = computeMidString(lastSortkey, '')
      lastSortkey = sortkey

      newCards.push({
        cardIndex: -1,
        eventId: event.id,
        eventIndex,
        description: schema.name,
        done: false,
        cardTypeId: aggregateRootCardType.id,
        releaseNo: null,
        sortkey,
        boundedContext: schema.boundedContext || null,
        schemaId: schema.id
      })
    }

    cardInput = {
      workflowId,
      updateNote: {
        action: 'linked aggregate roots',
        target: aggregateRootAssignments.map((assignment) => assignment.event.description).join(', ')
      },
      cards: newCards
    }
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: newSchemas,
    updateNote: {
      action: 'created entities',
      target: newSchemas.map((schema) => schema.name).join(', ')
    }
  }

  const aggregateRootCount = cardInput ? cardInput.cards.length : 0
  const skippedNote = skippedNames.length > 0
    ? ` Already present, left unchanged: ${skippedNames.join(', ')} — use update_entities to change their fields.`
    : ''

  if (newSchemas.length === 0) {
    return {
      schemaInput: null,
      cardInput: null,
      newSchemas: [],
      message: `Nothing to create.${skippedNote}`
    }
  }

  return {
    schemaInput,
    cardInput,
    newSchemas,
    message: `${newSchemas.length} ${newSchemas.length === 1 ? 'entity' : 'entities'} created successfully${aggregateRootCount > 0 ? `, ${aggregateRootCount} aggregate root link${aggregateRootCount === 1 ? '' : 's'} set` : ''}.${skippedNote}`,
    refs: newSchemas.map((schema) => ({ name: schema.name, ref: buildEntityRef(schema) }))
  }
}

export const buildUpdateEntitiesMutation = ({ workflow, workflowId, entities }) => {
  if (!Array.isArray(entities) || entities.length === 0) {
    return { error: 'entities is required and must be a non-empty array' }
  }

  const claimedSchemaIds = new Set()
  const schemaInputs = []
  const changeSummaries = []
  const refsOut = []

  for (const update of entities) {
    const {
      entity: schemaRef,
      name,
      description,
      boundedContext,
      addFields: rawAdds,
      updateFields: rawUpdates,
      removeFields
    } = update || {}

    if (!schemaRef) {
      return { error: 'Each item must include an entity ref' }
    }

    const resolved = resolveSchema(schemaRef, workflow)
    if (!resolved) {
      return { error: `Schema not found for ref: ${schemaRef}` }
    }
    const schemaId = resolved.id

    if (claimedSchemaIds.has(schemaId)) {
      return { error: `Entity "${schemaRef}" appears more than once in the batch` }
    }
    claimedSchemaIds.add(schemaId)

    const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
    if (schemaIndex === -1) {
      return { error: `Schema not found for ref: ${schemaRef}` }
    }

    if (boundedContext !== undefined && resolved.type !== SchemaType.ENTITY) {
      return { error: 'boundedContext can only be assigned to Entity schemas' }
    }

    const addFields = resolveFieldRefs(rawAdds, workflow)
    const updateFields = resolveFieldUpdateRefs(rawUpdates, workflow)

    const schema = workflow.schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in schema "${schema.name}"` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in schema "${schema.name}"` }
        }

        if (updates.name && updates.name !== fieldName) {
          const collision = currentFields.find((field) => field.name === updates.name)
          if (collision) {
            return { error: `Field "${updates.name}" already exists in schema "${schema.name}"` }
          }
        }

        const updatedField = { ...currentFields[fieldIndex] }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.description !== undefined) {
          updatedField.description = updates.description
        }
        if (updates.dataType !== undefined) {
          updatedField.dataType = updates.dataType
        }
        if (updates.isRequired !== undefined) {
          updatedField.tags = setTag(updatedField.tags, 'REQUIRED', updates.isRequired)
          // An optional field cannot be a key.
          if (!updates.isRequired) {
            updatedField.tags = setTag(updatedField.tags, 'KEY', false)
          }
        }
        if (updates.isKey !== undefined) {
          updatedField.tags = setTag(updatedField.tags, 'KEY', updates.isKey)
          // A key is always required.
          if (updates.isKey) {
            updatedField.tags = setTag(updatedField.tags, 'REQUIRED', true)
          }
        }
        if (updates.exampleData !== undefined) {
          updatedField.exampleData = updates.exampleData
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.relatedEntityId !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntityId
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        if (currentFields.find((existing) => existing.name === field.name)) {
          return { error: `Field "${field.name}" already exists in schema "${schema.name}"` }
        }
        currentFields.push(buildSchemaField(field, schema.type))
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    if (name !== undefined && name !== schema.name) {
      const collision = workflow.schemas.find((other) => other.name === name && other.type === schema.type && other.id !== schemaId)
      if (collision) {
        return { error: `Schema "${name}" of type ${schema.type} already exists` }
      }
      const collisionInBatch = schemaInputs.find((other) => other.name === name && other.type === schema.type)
      if (collisionInBatch) {
        return { error: `Schema "${name}" of type ${schema.type} is set by more than one entry in the batch` }
      }
      updatedSchema.name = name
      changes.push(`renamed to "${name}"`)
    }

    if (description !== undefined) {
      const normalizedSchemaDescription = normalizeDescription(description, MAX_DESCRIPTION_LENGTH)
      if (normalizedSchemaDescription.error) {
        return { error: `Entity "${schema.name}": ${normalizedSchemaDescription.error}` }
      }
      if (normalizedSchemaDescription.value) {
        updatedSchema.description = normalizedSchemaDescription.value
        changes.push('updated description')
      } else {
        updatedSchema.description = null
        changes.push('removed description')
      }
    }

    if (boundedContext !== undefined) {
      if (boundedContext) {
        const matchedBoundedContext = (workflow.boundedContexts || []).find((context) => context.name === boundedContext)
        if (!matchedBoundedContext) {
          return { error: `Bounded context "${boundedContext}" not found` }
        }
        updatedSchema.boundedContext = matchedBoundedContext.id
      } else {
        updatedSchema.boundedContext = null
      }
      changes.push('updated boundedContext')
    }

    if (changes.length === 0) {
      return { error: `No changes specified for "${schema.name}"` }
    }

    schemaInputs.push({
      ...updatedSchema,
      index: schemaIndex,
      expectedVersion: schema.version || null
    })
    changeSummaries.push(`"${schema.name}": ${changes.join(', ')}`)
    refsOut.push({
      name: updatedSchema.name,
      ref: buildEntityRef({ name: updatedSchema.name, fields: currentFields })
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: schemaInputs,
    updateNote: {
      action: 'updated entities',
      target: schemaInputs.map((schema) => schema.name).join(', ')
    }
  }

  const updatedCount = schemaInput.schemas.length

  return {
    schemaInput,
    changeSummaries,
    message: `${updatedCount} ${updatedCount === 1 ? 'entity' : 'entities'} updated: ${changeSummaries.join(' | ')}`,
    refs: refsOut
  }
}

export const buildDeleteEntityMutation = ({ workflow, workflowId, entity: schemaRef }) => {
  if (!schemaRef) {
    return { error: 'Schema ref is required' }
  }

  const resolved = resolveSchema(schemaRef, workflow)
  if (!resolved) {
    return { error: `Schema not found for ref: ${schemaRef}` }
  }
  const schemaId = resolved.id

  const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
  if (schemaIndex === -1) {
    return { error: 'Schema not found' }
  }

  const schema = workflow.schemas[schemaIndex]

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    deletedSchemas: [{ deleteIndex: schemaIndex, deleteId: schemaId }],
    updateNote: {
      action: 'deleted schema',
      target: schema.name
    }
  }

  // This tool deletes value objects as well as entities.
  const kind = hasIdField(schema.fields) ? 'Entity' : 'Value object'

  return { schemaInput, schema, message: `${kind} "${schema.name}" deleted successfully` }
}
